import { useState } from 'react';
import { clientExistant } from '../lib/contacts';
import { idProvisoire } from '../lib/identifiants';
import { C } from '../theme/palette';
import { BoutonContacts } from './BoutonContacts.jsx';
import { Btn } from './ui/Btn.jsx';
import { _inp } from './ui/Inputs.jsx';

const VIDE = { nom: '', adr: '', npa: '', tel: '', email: '' };

/**
 * Un client jamais vu, ajouté sur place : à la main, ou depuis les contacts du
 * téléphone — comme dans la nouvelle offre. Une facture faite directement,
 * sans offre, n'offrait que la liste des clients déjà connus.
 *
 * Le client reçoit un identifiant provisoire et part au serveur avec la
 * collection ; la pièce qui le désigne suit (voir `useSyncedCollection`).
 *
 * @param {object} props propriétés
 * @param {object[]} props.clients clients connus
 * @param {(fn: (p: object[]) => object[]) => void} props.setClients mise à jour des clients
 * @param {(id: number|string) => void} props.onChoisi reçoit le client créé ou retrouvé
 * @returns {import('react').ReactElement} les deux boutons et le formulaire
 */
export function NouveauClient({ clients, setClients, onChoisi }) {
  const [ouvert, setOuvert] = useState(false);
  const [fiche, setFiche] = useState(VIDE);
  const maj = (cle) => (e) => setFiche((p) => ({ ...p, [cle]: e.target.value }));

  const cree = (contact) => {
    // Le client déjà enregistré est repris tel quel : choisir deux fois la
    // même personne créait deux clients.
    const deja = clientExistant(clients, contact);
    if (deja) {
      onChoisi(deja.id);
      return;
    }
    const id = idProvisoire();
    setClients((p) => [...(p || []), { id, ...contact, remiseGlobale: 0, remises: {} }]);
    onChoisi(id);
  };

  return (
    <div style={{ marginBottom: 9 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <div style={{ flex: 1 }}>
          <Btn
            label="＋ Nouveau client"
            sm
            ghost
            color={C.navy}
            full
            onClick={() => setOuvert((v) => !v)}
          />
        </div>
        <BoutonContacts
          sm
          ghost
          color={C.teal}
          onContact={(contact) => {
            cree(contact);
            setOuvert(false);
          }}
          onEchec={() => setOuvert(true)}
        />
      </div>
      {ouvert && (
        <div
          style={{ border: '1px solid ' + C.border, borderRadius: 10, padding: 12, marginTop: 8 }}
        >
          <input
            value={fiche.nom}
            onChange={maj('nom')}
            placeholder="Nom du client *"
            aria-label="Nom du client"
            style={{ ..._inp, marginBottom: 7 }}
          />
          <input
            value={fiche.adr}
            onChange={maj('adr')}
            placeholder="Adresse"
            aria-label="Adresse du client"
            style={{ ..._inp, marginBottom: 7 }}
          />
          <input
            value={fiche.npa}
            onChange={maj('npa')}
            placeholder="NPA / Ville"
            aria-label="NPA et ville du client"
            style={{ ..._inp, marginBottom: 7 }}
          />
          <div style={{ display: 'flex', gap: 7, marginBottom: 8 }}>
            <input
              value={fiche.tel}
              onChange={maj('tel')}
              placeholder="Téléphone"
              aria-label="Téléphone du client"
              style={{ ..._inp }}
            />
            <input
              value={fiche.email}
              onChange={maj('email')}
              placeholder="E-mail"
              aria-label="E-mail du client"
              style={{ ..._inp }}
            />
          </div>
          <Btn
            label="✓ Créer le client"
            color={C.green}
            full
            disabled={!fiche.nom.trim()}
            onClick={() => {
              cree({
                nom: fiche.nom.trim(),
                adr: fiche.adr.trim(),
                npa: fiche.npa.trim(),
                tel: fiche.tel.trim(),
                email: fiche.email.trim(),
              });
              setFiche(VIDE);
              setOuvert(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
